import { makeValuesPredicate } from '@sgme/fp';
import clsx from 'clsx';
import { type FocusEvent, useCallback, useEffect, useRef, useState } from 'react';

import { getSeparatorsFromLocale, parseLocaleNumber } from '../../../utils/number.ts';
import { isDefined, isEmpty, isNotDefined, isNotEmpty } from '../../../utils/typescript-helpers.ts';
import { SgIcon } from '../../display/SgIcon';
import { SgTooltip } from '../../feedback/SgTooltip';
import type { SgNumberInputProps } from './types.ts';

const isValidInputKey = makeValuesPredicate(['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'End', 'Home']);
const isCopyPasteKey = makeValuesPredicate(['a', 'v', 'c', 'x']);
/**
 * SET
 * value (number | undefined) -> formattedValue (string)
 *
 * LOCAL CHANGE
 * formattedValue (string) -> numericValue (number | undefined) -> formattedValue (string)
 *
 * APPLY CHANGES
 * formattedValue (string) -> value (number | undefined)
 */

export const SgNumberInput = (props: SgNumberInputProps) => {
  const {
    value,
    placeholder,
    valuePosition = props.unitPosition ?? 'start',
    maxDecimals = 12,
    unit,
    unitIcon,
    unitBackgroundColor = 'discreet-primary',
    unitColor,
    unitPosition = 'start',
    unitTabNoIndex,
    onChange,
    onFocus,
    onBlur,
    onDirty,
    onUnitClick,
    readOnly,
    disabled,
    validationMessage,
    validationDisplay = 'aside',
    validationTooltipPosition = 'top',
    ref,
    tabNoIndex,
    locale = 'en',
    acceleratorsEnabled,
    selectAllOnFocus = false,
    allowNegative = true,
    integerOnly = false,
    signVisible = false,
    size = 'sm',
    state = 'default',
    color,
    className,
    'data-e2e': dataE2e,
    'data-e2e-is-modified': dataE2eIsModified,
  } = props;

  const formatValue = useCallback(
    (value: number | string): string => {
      return formatInputValueWithLocale(value ?? '', locale, allowNegative, integerOnly, maxDecimals, signVisible);
    },
    [allowNegative, integerOnly, locale, maxDecimals, signVisible],
  );

  const [inputValue, setInputValue] = useState(() => formatValue(value ?? ''));
  const [focusValue, setFocusValue] = useState(value ?? '');
  const [isDirty, setIsDirty] = useState(false);

  const inputElementRef = useRef<HTMLInputElement | null>(null);

  const parseValue = useCallback(
    (value: string): number | undefined => {
      return parseLocaleNumber(value ?? '', locale);
    },
    [locale],
  );

  // Synchronise inputValue with value prop
  useEffect(() => {
    setInputValue(formatValue(value ?? ''));
  }, [value, formatValue]);

  // Dirty state management
  useEffect(() => {
    if (onDirty) {
      onDirty(isDirty);
    }
  }, [isDirty, onDirty]);

  // Focus management
  const handleInputFocus = (event: FocusEvent<HTMLInputElement>) => {
    setFocusValue(value ?? '');
    setIsDirty(false);

    if (selectAllOnFocus) {
      event.target.select();
    }

    if (onFocus) {
      onFocus(event);
    }
  };

  // Blur management
  const handleInputBlur = () => {
    if (onBlur) {
      onBlur();
    }

    if (isDirty) {
      onChange?.(parseValue(inputValue));

      setIsDirty(false);
    }
  };

  // Change management
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;

    const parsedValue = formatValue(rawValue);

    setInputValue(parsedValue);
    setIsDirty(parsedValue !== focusValue);
  };

  // Keyboard management
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      inputElementRef.current?.blur();
      return;
    }

    if (event.key === 'Escape') {
      setInputValue(formatValue(focusValue));
      setIsDirty(false);

      event.preventDefault();

      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      const numericValue = parseLocaleNumber(inputValue, locale) ?? 0;

      if (!Number.isNaN(numericValue)) {
        const incrementedValue = event.key === 'ArrowUp' ? numericValue + 1 : numericValue - 1;

        const finalValue = !allowNegative && incrementedValue < 0 ? 0 : incrementedValue;

        setInputValue(formatValue(finalValue.toString()));
        setIsDirty(true);
      }

      event.preventDefault();

      return;
    }

    if (event.key === '-' && allowNegative) {
      const numericValue = parseLocaleNumber(inputValue, locale) ?? 0;

      if (!Number.isNaN(numericValue)) {
        const nextValue = numericValue === 0 ? '-' : formatValue((-numericValue).toString());

        setInputValue(nextValue);
        setIsDirty(true);
      }

      event.preventDefault();

      return;
    }

    const { decimalSeparator } = getSeparatorsFromLocale(locale);
    const otherDecimalSeparator = decimalSeparator === ',' ? '.' : ',';

    if ((event.key === decimalSeparator || event.key === otherDecimalSeparator) && !integerOnly) {
      if (!inputValue.includes(decimalSeparator)) {
        setInputValue(inputValue + decimalSeparator);
        setIsDirty(true);
      }

      event.preventDefault();

      return;
    }

    if (acceleratorsEnabled) {
      if (ACCELERATOR_KEYS.includes(event.key)) {
        const numericValue = parseLocaleNumber(inputValue, locale) ?? 0;

        if (numericValue === 0) {
          return;
        }

        // @ts-expect-error
        const factor = ACCELERATOR_FACTORS[event.key];

        if (!Number.isNaN(numericValue)) {
          const multipliedValue = numericValue * factor;

          setInputValue(formatValue(multipliedValue.toString()));
          setIsDirty(true);
        }

        event.preventDefault();

        return;
      }
    }

    if (isValidInputKey(event.key)) {
      // allowed
      return;
    }

    if (event.ctrlKey && isCopyPasteKey(event.key)) {
      // ctrl + a/v/c/x allowed
      return;
    }

    // if the key is not allowed (0-9 . ,), prevent the input
    const isNumberKey = /^\d$/.test(event.key);
    const isDecimalKey = (event.key === decimalSeparator || event.key === otherDecimalSeparator) && !integerOnly;
    const isNegativeKey = event.key === '-' && allowNegative;

    if (!isNumberKey && !isDecimalKey && !isNegativeKey) {
      event.preventDefault();
    }
  };

  // Clipboard management
  const handleInputCopy = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.clipboardData.setData(
      'text/plain',
      formatInputValueWithLocale(inputValue, locale, allowNegative, integerOnly, maxDecimals, signVisible),
    );

    event.preventDefault();
  };

  const handleInputCut = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.clipboardData.setData(
      'text/plain',
      formatInputValueWithLocale(inputValue, locale, allowNegative, integerOnly, maxDecimals, signVisible),
    );

    setInputValue('');
    setIsDirty(true);

    event.preventDefault();
  };

  const handleInputPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = event.clipboardData.getData('text');

    const parsedValue = formatInputValueWithLocale(pastedValue, locale, allowNegative, integerOnly, maxDecimals);

    setInputValue(parsedValue);
    setIsDirty(parsedValue !== focusValue);

    event.preventDefault();
  };

  // Accessibility
  const ariaInvalid = state === 'invalid' ? true : undefined;

  const unitClassName = clsx('input-group-text', {
    [`text-${unitColor}`]: isNotEmpty(unitColor),
    'text-small fs-12': size === 'sm',
    'text-medium fs-14': size === 'md',
    'text-large fs-16': size === 'lg',
    'text-xlarge fs-18': size === 'xl',
    'border-bottom border-info': state === 'edited',
    'border-bottom border-success': state === 'valid',
    'border-bottom border-danger': state === 'invalid',
    'border-bottom border-warning': state === 'warning',
  });

  const unitElement =
    isNotEmpty(unit) || isNotEmpty(unitIcon) ? (
      isDefined(onUnitClick) ? (
        <button
          data-e2e={isDefined(dataE2e) ? `${dataE2e}-unit` : undefined}
          onClick={onUnitClick}
          disabled={disabled}
          className={clsx(unitClassName, 'btn', `btn-${unitBackgroundColor}`)}
          type="button"
          tabIndex={unitTabNoIndex ? -1 : undefined}
        >
          {isNotEmpty(unitIcon) ? <SgIcon icon={unitIcon} size={size} /> : unit}
        </button>
      ) : (
        <span
          data-e2e={isDefined(dataE2e) ? `${dataE2e}-unit` : undefined}
          className={clsx(unitClassName, `bg-${unitBackgroundColor}`)}
        >
          {isNotEmpty(unitIcon) ? <SgIcon icon={unitIcon} size={size} /> : unit}
        </span>
      )
    ) : null;

  const input = (
    <input
      type="text"
      data-e2e={isDefined(dataE2e) ? `${dataE2e}-input` : undefined}
      data-e2e-is-modified={isDefined(dataE2eIsModified) ? dataE2eIsModified : undefined}
      value={inputValue}
      readOnly={readOnly}
      disabled={disabled}
      ref={(node) => {
        // ref?.current = node doesn't work -> TS2779: The left-hand side of an assignment expression may not be an optional property access
        if (ref !== undefined) {
          ref.current = node;
        }

        inputElementRef.current = node;
      }}
      className={clsx(className, `form-control form-control-${size}`, {
        'is-edited': state === 'edited',
        'is-valid': state === 'valid',
        'is-invalid': state === 'invalid',
        'is-warning': state === 'warning',
        'text-start': valuePosition === 'start',
        'text-end': valuePosition === 'end',
        [`text-${color}`]: isNotEmpty(color),
      })}
      placeholder={placeholder}
      tabIndex={tabNoIndex ? -1 : 0}
      aria-invalid={ariaInvalid}
      onFocus={handleInputFocus}
      onBlur={handleInputBlur}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onCopy={handleInputCopy}
      onCut={handleInputCut}
      onPaste={handleInputPaste}
    />
  );

  const inputGroup = (
    <div
      className={clsx('input-group', className, {
        'input-group-sm': size === 'sm',
        'input-group-md': size === 'md',
        'input-group-lg': size === 'lg',
        'input-group-xl': size === 'xl',
      })}
      data-e2e={dataE2e}
    >
      {unitPosition === 'start' && unitElement}
      {input}
      {unitPosition === 'end' && unitElement}
    </div>
  );

  if (isNotDefined(validationMessage) || isEmpty(validationMessage)) {
    return inputGroup;
  }

  return isNotEmpty(validationMessage) && validationDisplay === 'aside' ? (
    <div>
      {inputGroup}
      <div className="d-block invalid-feedback">{validationMessage}</div>
    </div>
  ) : (
    <SgTooltip mode="hover" backgroundColor="danger" arrow={true} placement={validationTooltipPosition}>
      <SgTooltip.Trigger>{inputGroup}</SgTooltip.Trigger>
      <SgTooltip.Content>
        <div className="p-1">{validationMessage}</div>
      </SgTooltip.Content>
    </SgTooltip>
  );
};

/**
 * Accelerator factor for keys (case insensitive):
 * - K for 1 000
 * - M for 1 000 000
 * - B for 1 000 000 000
 */
const ACCELERATOR_FACTORS = {
  k: 1e3,
  K: 1e3,

  m: 1e6,
  M: 1e6,

  b: 1e9,
  B: 1e9,
};

const ACCELERATOR_KEYS = Object.keys(ACCELERATOR_FACTORS);

/**
 * Formats a numeric value according to locale conventions while preserving user input context.
 *
 * This function intelligently formats numbers by:
 * - Applying locale-specific separators (decimal and group)
 * - Respecting user constraints (negative values, integer-only, max decimals)
 * - Preserving the number of decimal digits typed by the user during input
 * - Maintaining trailing decimal separators for better UX (e.g., "123." stays "123,")
 * - Displaying an explicit sign prefix when signVisible is true (e.g., "+1,234" for positive values)
 *
 * Why: Standard Intl.NumberFormat loses input context (trailing decimals, exact digit count).
 * This wrapper preserves that context to provide a natural typing experience.
 *
 * @param value - The numeric value to format (number or string)
 * @param locale - The locale for formatting (e.g., 'en-US', 'fr-FR')
 * @param allowNegative - Whether negative values are allowed
 * @param integerOnly - Whether to force integer-only formatting
 * @param maxDecimals - Maximum number of decimal digits allowed
 * @param signVisible - Whether to always show the sign (+ for positive, nothing for zero, - for negative)
 * @returns Formatted string respecting locale and preserving input context
 *
 * @example
 * formatInputValueWithLocale('1234.5', 'fr-FR', true, false, 2); // '1\u202f234,5'
 * formatInputValueWithLocale('123.', 'en-US', true, false, 2); // '123.' (preserves trailing decimal)
 * formatInputValueWithLocale(1234, 'en', true, false, undefined, true); // '+1,234'
 * formatInputValueWithLocale(-1234, 'en', true, false, undefined, true); // '-1,234'
 * formatInputValueWithLocale(0, 'en', true, false, undefined, true); // '0'
 */
export function formatInputValueWithLocale(
  value: string | number,
  locale: string,
  allowNegative: boolean,
  integerOnly: boolean,
  maxDecimals: number | undefined,
  signVisible = false,
) {
  // Return empty string for undefined/empty values
  if (isNotDefined(value) || value === '') {
    return '';
  }

  const { decimalSeparator, groupSeparator } = getSeparatorsFromLocale(locale);

  const originalString = typeof value === 'string' ? value : undefined;

  // Parse numeric value; if parsing fails, return empty string
  const numericValue = typeof value === 'number' ? value : (parseLocaleNumber(String(value), locale) ?? Number.NaN);

  if (Number.isNaN(numericValue)) {
    return '';
  }

  // Build formatting options based on user input and constraints
  const options = buildFormattingOptions(originalString, decimalSeparator, groupSeparator, integerOnly, maxDecimals);

  // Normalize -0 to 0 to avoid Intl.NumberFormat producing "-0".
  // Also normalize values that round to zero at the requested precision (e.g. -0.04 with maxDecimals=1
  // rounds to "-0.0" which should display as "0.0").
  const precision = options.maximumFractionDigits ?? (isDefined(maxDecimals) ? maxDecimals : 20);
  const roundedValue = Math.round(numericValue * 10 ** precision) / 10 ** precision;
  const normalizedValue = roundedValue === 0 ? 0 : numericValue;

  // If negative numbers are not allowed, format absolute value
  const valueToFormat = !allowNegative && normalizedValue < 0 ? -normalizedValue : normalizedValue;

  const formatted = new Intl.NumberFormat(locale, options).format(valueToFormat);

  // Preserve trailing decimal separator for better UX during input
  const shouldPreserveTrailingDecimal = shouldPreserveDecimalSeparator(originalString, decimalSeparator);

  const formattedWithTrailingDecimal = shouldPreserveTrailingDecimal ? formatted + decimalSeparator : formatted;

  // Prepend explicit "+" sign for strictly positive values when signVisible is enabled
  if (signVisible && normalizedValue > 0) {
    return `+${formattedWithTrailingDecimal}`;
  }

  return formattedWithTrailingDecimal;
}

/**
 * Determines if a trailing decimal separator should be preserved in the formatted output.
 * This improves UX by keeping the separator visible while the user types decimal digits.
 */
function shouldPreserveDecimalSeparator(originalString: string | undefined, decimalSeparator: string): boolean {
  if (!originalString) {
    return false;
  }

  const trimmed = originalString.trim();
  const otherDecimal = decimalSeparator === ',' ? '.' : ',';

  return trimmed.endsWith(decimalSeparator) || trimmed.endsWith(otherDecimal);
}

/**
 * Builds Intl.NumberFormatOptions based on the original input string and constraints.
 * Preserves the number of decimal digits typed by the user when possible.
 */
function buildFormattingOptions(
  originalString: string | undefined,
  decimalSeparator: string,
  groupSeparator: string,
  integerOnly: boolean,
  maxDecimals: number | undefined,
): Intl.NumberFormatOptions {
  const options: Intl.NumberFormatOptions = {};

  if (integerOnly) {
    options.maximumFractionDigits = 0;
    options.minimumFractionDigits = 0;

    return options;
  }

  // If no original string, apply maxDecimals constraint only
  if (!originalString) {
    if (isDefined(maxDecimals)) {
      options.maximumFractionDigits = maxDecimals;
    }

    return options;
  }

  const otherDecimal = decimalSeparator === ',' ? '.' : ',';
  const hasDecimal = originalString.includes(decimalSeparator) || originalString.includes(otherDecimal);

  // If original string has no decimal, apply maxDecimals constraint
  if (!hasDecimal) {
    if (isDefined(maxDecimals)) {
      options.maximumFractionDigits = maxDecimals;
    }

    return options;
  }

  // Normalize and count fraction digits from original input
  const normalized = normalizeDecimalString(originalString, decimalSeparator, groupSeparator, otherDecimal);
  const decimalIndex = normalized.indexOf(decimalSeparator);

  if (decimalIndex >= 0) {
    const fractionDigitsCount = countFractionDigits(normalized, decimalIndex);
    const cappedFractionDigits = isDefined(maxDecimals)
      ? Math.min(fractionDigitsCount, maxDecimals)
      : fractionDigitsCount;

    options.minimumFractionDigits = cappedFractionDigits;
    options.maximumFractionDigits = cappedFractionDigits;
  } else if (isDefined(maxDecimals)) {
    options.maximumFractionDigits = maxDecimals;
  }

  return options;
}

/**
 * Normalizes a decimal string by removing group separators and standardizing the decimal
 * separator.
 */
function normalizeDecimalString(
  input: string,
  decimalSeparator: string,
  groupSeparator: string,
  otherDecimal: string,
): string {
  return input
    .split(groupSeparator)
    .join('') // Remove locale-specific group separator
    .split('\u00A0')
    .join('') // Remove non-breaking spaces
    .split(otherDecimal)
    .join(decimalSeparator); // Standardize decimal separator
}

/**
 * Counts the number of digit characters after the decimal separator.
 */
function countFractionDigits(normalized: string, decimalIndex: number): number {
  const fractionPart = normalized.slice(decimalIndex + 1);
  const digitOnlyFraction = fractionPart.replace(/\D/g, '');

  return digitOnlyFraction.length;
}
